import type {
  TClientePessoaFisicaPropostaSchema,
  TClientePessoaJuridicaPropostaSchema,
  TCompradorPropostaSchema,
  TProcuradorPropostaSchema,
} from '@/features/proposta/brasil/domain/schemas'
import type { TTipoDoVinculoDoCompradorEnum } from '@/types/proposta'

export type TTipoClienteProposta = 'Comprador' | TTipoDoVinculoDoCompradorEnum

export interface ClienteParaExclusao {
  id: string
  nome: string
  tipo: TTipoClienteProposta
}

export interface FieldArrayDeleteCompradorData {
  clientes: TClientePessoaFisicaPropostaSchema[]
  compradores: TCompradorPropostaSchema[]
  procuradores: TProcuradorPropostaSchema[]
  clientesJuridicos: TClientePessoaJuridicaPropostaSchema[]
}

export interface FieldArrayDeleteCompradorActions {
  removeCliente: (index: number) => void
  removeComprador: (index: number) => void
  removeProcurador: (index: number) => void
  removeClienteJuridico: (index: number) => void
  updateComprador: (index: number, comprador: TCompradorPropostaSchema) => void
  updateProcurador: (index: number, procurador: TProcuradorPropostaSchema) => void
}

export class ExcluirCompradorValidator {
  static validar(
    cliente: ClienteParaExclusao,
    compradorId: string,
    data: FieldArrayDeleteCompradorData,
  ): string | null {
    switch (cliente.tipo) {
      case 'Comprador':
        return null
      case 'Procurador':
        return this.validarProcurador(cliente.id, data)
      default:
        return this.validarVinculo(cliente, compradorId, data)
    }
  }

  private static validarProcurador(procuradorId: string, data: FieldArrayDeleteCompradorData): string | null {
    const procurador = data.procuradores.find((p) => p.procuradorId === procuradorId)
    if (!procurador) return null

    const clienteDependente = this.encontrarClienteDependenteDeProcurador(procurador, procuradorId, data)

    if (clienteDependente)
      return 'Para comprador pessoa jurídica é obrigatório ter pelo menos um representante legal ou procurador cadastrado'

    return null
  }

  private static encontrarClienteDependenteDeProcurador(
    procurador: TProcuradorPropostaSchema,
    procuradorId: string,
    data: FieldArrayDeleteCompradorData,
  ): string | undefined {
    return procurador.clientesIds.find((clienteId: string) => {
      const ehClienteJuridico = data.clientesJuridicos.some((_clienteJuridico) => _clienteJuridico.id === clienteId)
      if (!ehClienteJuridico) return false

      const comprador = data.compradores.find((_comprador) => _comprador.clienteId === clienteId)
      const temRepresentante = comprador?.vinculos?.some((_vinculo) => _vinculo.tipo === 'Representante')
      const temOutroProcurador = data.procuradores.some(
        (p) => p.procuradorId !== procuradorId && p.clientesIds.includes(clienteId),
      )

      return !temRepresentante && !temOutroProcurador
    })
  }

  private static validarVinculo(
    cliente: ClienteParaExclusao,
    compradorId: string,
    data: FieldArrayDeleteCompradorData,
  ): string | null {
    const comprador = this.obterCompradorDoVinculo(cliente.id, compradorId, data)
    if (!comprador) return null

    const tipo = cliente.tipo as Exclude<TTipoDoVinculoDoCompradorEnum, 'Procurador'>

    switch (tipo) {
      case 'Conjuge':
        return this.validarConjuge(compradorId, data)
      case 'MaePai':
        return this.validarMaePai(cliente.id, compradorId, data)
      case 'Representante':
        return this.validarRepresentanteLegal(cliente.id, compradorId, data)
      default:
        return null
    }
  }

  private static obterCompradorDoVinculo(
    clienteId: string,
    compradorId: string,
    data: FieldArrayDeleteCompradorData,
  ): TCompradorPropostaSchema | undefined {
    return data.compradores.find((c) => compradorId === c.clienteId)
  }

  private static validarConjuge(compradorId: string, data: FieldArrayDeleteCompradorData): string | null {
    const comprador = data.compradores.find((c) => c.clienteId === compradorId)

    if (comprador?.vinculos?.some((v) => v.tipo === 'Conjuge'))
      return 'Para comprador casado com assinatura dupla é obrigatório ter pelo menos um cônjuge cadastrado.'

    return null
  }

  private static validarMaePai(
    clienteId: string,
    compradorId: string,
    data: FieldArrayDeleteCompradorData,
  ): string | null {
    const comprador = data.compradores.find((c) => c.clienteId === compradorId)

    const temOutroMaePai = comprador?.vinculos?.some((v) => v.tipo === 'MaePai' && v.clienteId !== clienteId)

    if (!temOutroMaePai) return 'Para comprador menor de 18 anos é obrigatório ter pelo menos uma mãe/pai cadastrada.'

    return null
  }

  private static validarRepresentanteLegal(
    clienteId: string,
    compradorId: string,
    data: FieldArrayDeleteCompradorData,
  ): string | null {
    const ehClienteJuridico = data.clientesJuridicos.some((cj) => cj.id === compradorId)
    if (!ehClienteJuridico) return null

    const comprador = data.compradores.find((c) => c.clienteId === compradorId)

    const temOutroRepresentante = comprador?.vinculos?.some(
      (v) => v.tipo === 'Representante' && v.clienteId !== clienteId,
    )
    const temProcurador = data.procuradores.some((p) => p.clientesIds.includes(compradorId))

    if (!temOutroRepresentante && !temProcurador)
      return 'Para comprador pessoa jurídica é obrigatório ter pelo menos um representante legal ou procurador cadastrado'

    return null
  }
}
